import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import Navbar from '../../components/Navbar';

/* ─────────────────────────────────────────────────────────────────────────────
   DESIGN SYSTEM — mirrors Home.jsx exactly
   Amber/Orange  → from-amber-600/90  to-orange-600/90   shadow amber-0.3
   Blue/Indigo   → from-blue-700/90   to-indigo-700/90   shadow blue-0.3
   Glass panels  → bg-slate-900/40 backdrop-blur-md border-white/10
───────────────────────────────────────────────────────────────────────────── */

const formatDistance = (km) => {
  if (km === null || km === undefined) return null;
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km} km`;
};

/* ── Supplier Detail Modal ──────────────────────────────────────────────── */
const SupplierModal = ({ supplier, onClose }) => {
  if (!supplier) return null;

  const tryParse = (v) => { try { return typeof v === 'string' ? JSON.parse(v) : v; } catch { return null; } };
  const photos = tryParse(supplier.product_photos) || [];
  const coverage = tryParse(supplier.service_coverage_area) || {};
  const areas = Object.entries(coverage).filter(([, v]) => v).map(([k]) => k);

  const certs = [
    supplier.gst_registered && 'GST',
    supplier.msme_registered && 'MSME',
    supplier.fssai_registered && 'FSSAI',
    supplier.iso_registered && 'ISO',
  ].filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/75 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div
        className="relative w-full max-w-3xl rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(15,23,42,0.90)',
          backdropFilter: 'blur(28px)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 16px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05) inset',
        }}
      >
        {/* ── Header band — blue/indigo gradient (matches "Product Search" CTA) */}
        <div
          className="relative px-6 py-5 flex items-center justify-between"
          style={{
            background: 'linear-gradient(135deg, rgba(29,78,216,0.85) 0%, rgba(67,56,202,0.85) 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
          }}
        >
          {/* top shimmer */}
          <div className="absolute top-0 inset-x-0 h-px"
            style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent)' }} />

          <div className="flex items-center space-x-4">
            {supplier.logo_url ? (
              <img src={supplier.logo_url} alt="" className="w-14 h-14 rounded-xl object-cover border-2 border-white/20 shadow-lg" />
            ) : (
              <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-2xl font-bold shadow-lg"
                style={{ background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.25)' }}>
                {supplier.business_name?.charAt(0)}
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-white drop-shadow">{supplier.business_name}</h2>
              {supplier.brand_name && <p className="text-blue-200 text-sm font-medium">Brand: {supplier.brand_name}</p>}
              <p className="text-blue-200/70 text-sm">{[supplier.city, supplier.state, supplier.head_office_country].filter(Boolean).join(' · ')}</p>
            </div>
          </div>

          <button onClick={onClose}
            className="text-white/60 hover:text-white transition-colors w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.15)' }}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-5">

          {/* Distance + Certs row */}
          <div className="flex flex-wrap gap-2 items-center">
            {supplier.distance_km !== null && (
              <span className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-sm font-bold uppercase tracking-wide"
                style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.35)', color: '#fbbf24' }}>
                📍 {formatDistance(supplier.distance_km)} away
              </span>
            )}
            {certs.map(c => (
              <span key={c} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
                style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', color: '#86efac' }}>
                ✓ {c}
              </span>
            ))}
          </div>

          {/* Glass section helper */}
          {[
            { title: 'About the Business', body: supplier.description },
            {
              title: 'Products & Services',
              body: supplier.products_services_offered,
              extra: supplier.key_product_service && <p className="mt-2 text-amber-400 text-sm font-semibold">⭐ Key: {supplier.key_product_service}</p>
            },
            { title: 'Why Choose Us (USP)', body: supplier.usp_strengths },
            { title: 'Awards & Achievements', body: supplier.awards_achievements },
          ].filter(s => s.body).map(s => (
            <GlassSection key={s.title} title={s.title}>
              <p className="text-white/75 text-sm leading-relaxed">{s.body}</p>
              {s.extra}
            </GlassSection>
          ))}

          {/* Info grid */}
          {[
            { label: 'Business Model', value: supplier.business_model },
            { label: 'Category', value: supplier.primary_business_category },
            { label: 'Est. Year', value: supplier.year_of_establishment },
            { label: 'Industry Served', value: supplier.industry_served },
            { label: 'Business Type', value: supplier.business_type },
            { label: 'Legal Entity', value: supplier.legal_entity_type },
          ].some(i => i.value) && (
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Business Model', value: supplier.business_model },
                  { label: 'Category', value: supplier.primary_business_category },
                  { label: 'Est. Year', value: supplier.year_of_establishment },
                  { label: 'Industry Served', value: supplier.industry_served },
                  { label: 'Business Type', value: supplier.business_type },
                  { label: 'Legal Entity', value: supplier.legal_entity_type },
                ].filter(i => i.value).map(i => (
                  <div key={i.label} className="rounded-xl p-3"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1">{i.label}</p>
                    <p className="text-sm font-semibold text-white/90">{i.value}</p>
                  </div>
                ))}
              </div>
            )}

          {/* Coverage areas */}
          {areas.length > 0 && (
            <GlassSection title="Service Coverage">
              <div className="flex flex-wrap gap-2">
                {areas.map(a => (
                  <span key={a} className="text-xs font-medium px-3 py-1 rounded-full"
                    style={{ background: 'rgba(29,78,216,0.2)', border: '1px solid rgba(59,130,246,0.35)', color: '#93c5fd' }}>
                    {a}
                  </span>
                ))}
              </div>
            </GlassSection>
          )}

          {/* Photos */}
          {photos.length > 0 && (
            <div>
              <SectionLabel>Photos</SectionLabel>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {photos.slice(0, 6).map((url, i) => (
                  <img key={i} src={url} alt="" className="h-24 w-full object-cover rounded-xl"
                    style={{ border: '1px solid rgba(255,255,255,0.08)' }} />
                ))}
              </div>
            </div>
          )}

          {/* Contact */}
          <GlassSection title="Contact Information">
            <div className="space-y-2.5">
              {[
                { icon: 'person', color: '#60a5fa', val: supplier.owner_name, render: v => <span className="text-white/80 text-sm">{v}</span> },
                { icon: 'phone', color: '#60a5fa', val: supplier.phone, render: v => <a href={`tel:${v}`} className="text-blue-300 hover:text-blue-200 transition-colors text-sm">{v}</a> },
                { icon: 'email', color: '#60a5fa', val: supplier.email, render: v => <a href={`mailto:${v}`} className="text-blue-300 hover:text-blue-200 transition-colors text-sm">{v}</a> },
                {
                  icon: 'whatsapp', color: '#4ade80', val: supplier.whatsapp_number,
                  render: v => <a href={`https://wa.me/${v.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 transition-colors text-sm">WhatsApp: {v}</a>
                },
                {
                  icon: 'map', color: '#60a5fa', val: supplier.business_address,
                  render: v => <span className="text-white/60 text-sm">{v}{supplier.pincode ? ` — ${supplier.pincode}` : ''}</span>
                },
                {
                  icon: 'web', color: '#60a5fa', val: supplier.company_website_url,
                  render: v => <a href={v} target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-200 transition-colors text-sm truncate">{v}</a>
                },
              ].filter(r => r.val).map(r => (
                <div key={r.icon} className="flex items-start space-x-2">
                  <ContactIcon type={r.icon} color={r.color} />
                  {r.render(r.val)}
                </div>
              ))}
              {supplier.google_map_location && (
                <a href={supplier.google_map_location} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center space-x-1.5 text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors mt-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <span>Open in Google Maps</span>
                </a>
              )}
            </div>
          </GlassSection>
        </div>
      </div>
    </div>
  );
};

/* ── Small helpers ──────────────────────────────────────────────────────── */
const GlassSection = ({ title, children }) => (
  <div className="rounded-xl p-4"
    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
    <SectionLabel>{title}</SectionLabel>
    <div className="mt-2">{children}</div>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-bold uppercase tracking-widest text-blue-400 mb-1">{children}</p>
);

const ContactIcon = ({ type, color }) => {
  const paths = {
    person: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    phone: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
    email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    map: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z',
    web: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9',
    whatsapp: null,
  };
  if (type === 'whatsapp') return (
    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill={color} viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
  return (
    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke={color} strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d={paths[type]} />
    </svg>
  );
};

/* ── Supplier Card ──────────────────────────────────────────────────────── */
const SupplierCard = ({ supplier, onViewMore }) => (
  <div
    className="relative rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.025] group"
    style={{
      background: 'rgba(15,23,42,0.50)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255,255,255,0.10)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
    }}
  >
    {/* Top shimmer line that brightens on hover */}
    <div className="absolute top-0 inset-x-0 h-px transition-opacity duration-300 opacity-60 group-hover:opacity-100"
      style={{ background: 'linear-gradient(90deg,transparent,rgba(245,158,11,0.7),rgba(59,130,246,0.7),transparent)' }} />

    <div className="p-5">
      <div className="flex items-start space-x-4">
        {/* Avatar / Logo */}
        {supplier.logo_url ? (
          <img src={supplier.logo_url} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
            style={{ border: '1px solid rgba(255,255,255,0.12)' }} />
        ) : (
          <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-extrabold flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg,rgba(29,78,216,0.7) 0%,rgba(67,56,202,0.7) 100%)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}>
            {supplier.business_name?.charAt(0)}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <h3 className="text-base font-bold text-white leading-tight">{supplier.business_name}</h3>
              {supplier.primary_business_category &&
                <p className="text-xs font-semibold text-blue-300 mt-0.5 uppercase tracking-wide">{supplier.primary_business_category}</p>}
            </div>

            {/* Distance badge — amber/orange, matches "Become a Vendor" */}
            {supplier.distance_km !== null && (
              <span className="flex-shrink-0 flex items-center space-x-1 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide"
                style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)', color: '#fb923c' }}>
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span>{formatDistance(supplier.distance_km)}</span>
              </span>
            )}
          </div>

          <p className="text-sm text-white/40 mt-1">
            {[supplier.city, supplier.state, supplier.head_office_country].filter(Boolean).join(' · ')}
          </p>

          {supplier.key_product_service &&
            <p className="text-sm text-white/60 mt-2 line-clamp-2">{supplier.key_product_service}</p>}
        </div>
      </div>

      {/* Bottom row */}
      <div className="mt-4 pt-4 flex items-center justify-between"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Cert badges */}
        <div className="flex gap-1.5 flex-wrap">
          {[
            supplier.gst_registered && 'GST',
            supplier.msme_registered && 'MSME',
            supplier.iso_registered && 'ISO',
          ].filter(Boolean).map(c => (
            <span key={c} className="text-[11px] px-2 py-0.5 rounded font-bold uppercase tracking-wide"
              style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.25)', color: '#86efac' }}>
              ✓ {c}
            </span>
          ))}
        </div>

        {/* View More — blue/indigo gradient, exact Home "Product Search" style */}
        <button
          onClick={() => onViewMore(supplier)}
          className="group/btn relative px-6 py-2 rounded-full overflow-hidden transition-all duration-300 hover:scale-105"
          style={{ boxShadow: '0 8px_32px rgba(59,130,246,0.30)', border: '1px solid rgba(255,255,255,0.15)' }}
        >
          <div className="absolute inset-0" style={{ background: 'rgba(15,23,42,0.25)', backdropFilter: 'blur(4px)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg,rgba(29,78,216,0.90) 0%,rgba(67,56,202,0.90) 100%)' }} />
          <span className="relative flex items-center space-x-1.5 text-white text-xs font-bold uppercase tracking-widest z-10 drop-shadow-md">
            <span>View More</span>
            <svg className="w-3.5 h-3.5 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </span>
        </button>
      </div>
    </div>
  </div>
);

/* ── Main Page ──────────────────────────────────────────────────────────── */
const UserProducts = () => {
  const [form, setForm] = useState({ country: '', pincode: '', keyword: '' });
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState(null);
  const [locationBlocked, setLocationBlocked] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const doSearch = async (lat, lng) => {
    setLoading(true); setLocationBlocked(false);
    try {
      const p = new URLSearchParams({
        country: form.country.trim(),
        pincode: form.pincode.trim(),
        keyword: form.keyword.trim(),
        ...(lat !== null ? { lat, lng } : {}),
      });
      const res = await fetch(`${API_URL}/supplier/search?${p}`);
      const data = await res.json();
      if (data.success) setSuppliers(data.suppliers || []);
      else { toast.error(data.message || 'Search failed'); setSuppliers([]); }
    } catch { toast.error('Connection error. Try again.'); setSuppliers([]); }
    setLoading(false); setSearched(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!form.country.trim() && !form.pincode.trim() && !form.keyword.trim())
      return toast.error('Fill in at least one search field.');

    // Location is MANDATORY — block search if not supported
    if (!('geolocation' in navigator)) {
      setLocationBlocked(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      // ✅ Location granted — proceed with search
      (pos) => doSearch(pos.coords.latitude, pos.coords.longitude),
      // ❌ Location denied — block entirely, no fallback
      () => setLocationBlocked(true),
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  /* Input style helper */
  const inputStyle = { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' };

  return (
    /* ── bg matches Home: bg-slate-900 ──────────────────────────────── */
    <div className="min-h-screen bg-slate-900 relative overflow-x-hidden font-sans">

      {/* Ambient blobs */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle,rgba(245,158,11,0.5) 0%,transparent 65%)', filter: 'blur(80px)' }} />
        <div className="absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle,rgba(59,130,246,0.5) 0%,transparent 65%)', filter: 'blur(80px)' }} />
        <div className="absolute -bottom-20 left-1/3 w-80 h-80 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle,rgba(99,102,241,0.5) 0%,transparent 65%)', filter: 'blur(70px)' }} />
      </div>

      <div className="relative z-10">
        <Navbar userType="user" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">

          {/* ── Page Title ─────────────────────────────────────────── */}
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight drop-shadow-lg leading-tight">
              Discover Tamil{' '}
              <span className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg,#f59e0b,#f97316)' }}>
                Suppliers
              </span>
            </h1>
            <p className="mt-3 text-base text-white/40 max-w-lg mx-auto">
              Search verified, active businesses by country, pincode or product — sorted by distance from you
            </p>
          </div>

          {/* ── Search Form Glass Card ──────────────────────────────── */}
          <form onSubmit={handleSearch}
            className="relative rounded-2xl overflow-hidden mb-8 p-6 md:p-8"
            style={{
              background: 'rgba(15,23,42,0.55)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 8px 40px rgba(0,0,0,0.45)',
            }}
          >
            {/* Top amber shimmer — matches Home top bar accent */}
            <div className="absolute top-0 inset-x-0 h-px"
              style={{ background: 'linear-gradient(90deg,transparent,rgba(245,158,11,0.8),rgba(251,146,60,0.8),transparent)' }} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { name: 'country', label: 'Country', placeholder: 'India, UAE, Singapore…' },
                { name: 'pincode', label: 'Pincode / ZIP', placeholder: '600001, 110001…' },
                { name: 'keyword', label: 'Product / Service', placeholder: 'Textiles, IT, Food…' },
              ].map(f => (
                <div key={f.name}>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-white/40 mb-2">
                    {f.label}
                  </label>
                  <input
                    type="text" name={f.name} value={form[f.name]} placeholder={f.placeholder}
                    autoComplete="off"
                    onChange={e => setForm(p => ({ ...p, [e.target.name]: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl text-sm placeholder-white/25 outline-none transition-all duration-200"
                    style={inputStyle}
                    onFocus={e => { e.target.style.border = '1px solid rgba(245,158,11,0.55)'; e.target.style.boxShadow = '0 0 0 3px rgba(245,158,11,0.10)'; }}
                    onBlur={e => { e.target.style.border = '1px solid rgba(255,255,255,0.12)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-white/30 flex items-center gap-1.5">
                <svg className="w-4 h-4 text-amber-500/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                GPS will be used to sort results nearest-first
              </p>

              {/* ── Search button — amber/orange gradient, "Become a Vendor" style ── */}
              <button
                type="submit" disabled={loading}
                className="group relative w-full sm:w-auto px-10 py-4 rounded-full overflow-hidden transition-all duration-300 hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
                style={{ boxShadow: '0 8px 32px rgba(245,158,11,0.30)', border: '1px solid rgba(255,255,255,0.20)' }}
              >
                {/* glass backdrop */}
                <div className="absolute inset-0" style={{ background: 'rgba(15,23,42,0.20)', backdropFilter: 'blur(4px)' }} />
                {/* amber → orange gradient — same as Home "Become a Vendor" */}
                <div className="absolute inset-0 transition-opacity duration-300"
                  style={{ background: 'linear-gradient(135deg,rgba(217,119,6,0.95) 0%,rgba(234,88,12,0.95) 100%)' }} />
                <span className="relative flex items-center justify-center gap-2 text-white font-bold text-sm uppercase tracking-widest z-10 drop-shadow-md">
                  {loading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Searching…
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Search Suppliers
                      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </>
                  )}
                </span>
              </button>
            </div>
          </form>

          {/* ── Location BLOCKED — full blocking card ────────────────── */}
          {locationBlocked && (
            <div className="rounded-2xl overflow-hidden"
              style={{
                background: 'rgba(15,23,42,0.65)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(245,158,11,0.30)',
                boxShadow: '0 8px 40px rgba(245,158,11,0.10)',
              }}
            >
              {/* amber shimmer top */}
              <div className="h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(245,158,11,0.9),rgba(251,146,60,0.9),transparent)' }} />

              <div className="flex flex-col items-center text-center px-8 py-12 space-y-5">
                {/* Icon */}
                <div className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(245,158,11,0.12)', border: '2px solid rgba(245,158,11,0.35)' }}>
                  <svg className="w-10 h-10 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Location Access Required</h3>
                  <p className="text-white/50 text-sm max-w-sm leading-relaxed">
                    We need your location to find and sort suppliers near you.
                    Please allow location access to continue.
                  </p>
                </div>

                {/* Steps */}
                <div className="rounded-xl px-6 py-4 text-left w-full max-w-sm space-y-2"
                  style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.15)' }}>
                  <p className="text-xs font-bold uppercase tracking-widest text-amber-400 mb-3">How to enable location</p>
                  {[
                    'Click the 🔒 lock icon in your browser address bar',
                    'Find "Location" and set it to "Allow"',
                    'Refresh the page and search again',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm text-white/60">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold mt-0.5"
                        style={{ background: 'rgba(245,158,11,0.20)', color: '#fbbf24' }}>{i + 1}</span>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>

                {/* Retry button — amber/orange style */}
                <button
                  onClick={() => { setLocationBlocked(false); }}
                  className="group relative px-8 py-3 rounded-full overflow-hidden transition-all duration-300 hover:scale-105"
                  style={{ boxShadow: '0 8px 32px rgba(245,158,11,0.30)', border: '1px solid rgba(255,255,255,0.20)' }}
                >
                  <div className="absolute inset-0" style={{ background: 'rgba(15,23,42,0.20)', backdropFilter: 'blur(4px)' }} />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg,rgba(217,119,6,0.95),rgba(234,88,12,0.95))' }} />
                  <span className="relative flex items-center gap-2 text-white text-sm font-bold uppercase tracking-widest z-10 drop-shadow-md">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Try Again
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* ── Loading spinner ─────────────────────────────────────── */}
          {loading && (
            <div className="text-center py-24">
              <svg className="animate-spin h-12 w-12 text-amber-400 mx-auto opacity-80" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="mt-4 text-white/30 text-sm uppercase tracking-widest">Finding suppliers near you…</p>
            </div>
          )}

          {/* ── No results ─────────────────────────────────────────── */}
          {!loading && searched && suppliers.length === 0 && (
            <div className="text-center py-20 rounded-2xl"
              style={{ background: 'rgba(15,23,42,0.40)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="text-5xl mb-4">🔍</p>
              <h3 className="text-lg font-semibold text-white/60">No suppliers found</h3>
              <p className="text-sm text-white/30 mt-2">Try a different keyword, pincode, or country.</p>
            </div>
          )}

          {/* ── Results ────────────────────────────────────────────── */}
          {!loading && suppliers.length > 0 && (
            <>
              {/* results header divider */}
              <div className="flex items-center gap-4 mb-6">
                <p className="text-[11px] font-bold uppercase tracking-widest text-white/35 whitespace-nowrap">
                  {suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''} found
                  {suppliers[0]?.distance_km !== null && ' · Nearest first'}
                </p>
                <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg,rgba(255,255,255,0.12),transparent)' }} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {suppliers.map(s => <SupplierCard key={s.id} supplier={s} onViewMore={setSelected} />)}
              </div>
            </>
          )}

          {/* ── Pre-search empty state ──────────────────────────────── */}
          {!searched && !loading && (
            <div className="text-center py-24">
              <p className="text-7xl mb-5 opacity-20">🏪</p>
              <p className="text-white/25 text-sm uppercase tracking-widest">Use the form above to search for suppliers</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {selected && <SupplierModal supplier={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default UserProducts;